// Цвета линий карты: изотермы, фронты, волнение, снос, маркеры судов.
//
// ЕДИНСТВЕННОЕ место, где у карты есть свой цвет. Всё остальное — заливка
// слоёв — приезжает опорами с сервера (`palette` в каталоге слоёв); линии же
// геометрию берут с сервера, а рисует их MapLibre, и цвет ему нужен готовым:
// CSS-переменных он не понимает, `rgb(var(--accent))` роняет добавление слоя.
//
// Одна гамма на бренд, без деления по темам (решение владельца 2026-08-14).
// Линии лежат поверх КАРТЫ, а не поверх поверхности интерфейса: графитовая
// изотерма с белой обводкой работает и на глубокой воде тёмной темы, и на
// светлой подложке. Второй повод прозаичнее — цвета по темам потребовали бы
// перекрашивать слои руками на каждом переключении темы.
//
// Умолчания повторяют то, чем карта крашена сегодня, слово в слово: домен без
// своей гаммы обязан выглядеть в точности как до появления бренд-паков.
import type { SiteBrand } from '../i18n'

export type MapColorKey =
  | 'isotherm'
  | 'isotherm-surface'
  | 'front'
  | 'wave'
  | 'hsi-arrow'
  | 'earthquake'
  | 'vessel-fresh'
  | 'vessel-stale'
  | 'catch'
  | 'eez'
  | 'fishing-district'
  | 'fishing-ban'
  | 'export-plate'

/** Ключи со ШКАЛОЙ (список цветов) вместо одного цвета. */
export type MapRampKey = 'drift' | 'vessel-ops'

export const DEFAULT_MAP_COLORS: Record<MapColorKey, string> = {
  isotherm: '#111827',       // тёмный графит
  // Поверхностная изотерма. Отдельная линия отдельного вопроса: глубинная
  // отвечает про горизонт Контекста, эта — про 0 м. Фиолетовый не занят другими
  // линиями карты и читается на обоих концах coolwarm под белой подложкой.
  'isotherm-surface': '#6d28d9',
  front: '#facc15',          // насыщенный жёлтый
  wave: '#e42e22',           // красный «занят» волной
  'hsi-arrow': '#ffffff',
  earthquake: '#e11d48',
  'vessel-fresh': '#f97316',
  'vessel-stale': '#9ca3af',
  catch: '#38bdf8',
  // Рубежи экономических зон — ОДИН тон на все типы линии: спорных рубежей в
  // «Лоции» нет (решение владельца 07.09.2026), различает их начертание.
  // Фуксия — не вкус, а конвенция морских карт:
  // административные и регуляторные границы на них рисуют пурпуром, и ни один
  // другой слой этот тон не занимает (изотерма поверхности — фиолетовая #6d28d9,
  // заметно синее).
  eez: '#c026d3',
  // Промысловые районы. Тёмная бирюза: соседство с рубежами ИЭЗ (фуксия)
  // намеренно контрастное — это ДВА РАЗНЫХ вопроса на одной карте («в чьей я
  // воде» и «по каким правилам тут ловят»), и слить их в один тон значило бы
  // подсказать, что это одно и то же. Ни один другой линейный слой бирюзу не
  // занимает: течения красятся шкалой, фронт жёлтый, волна красная.
  'fishing-district': '#0d9488',
  // Запретные районы. Тёмно-красный — «здесь нельзя», и по светлоте он далеко
  // от ярко-красной волны (#e42e22) и розового землетрясения (#e11d48), а с
  // бирюзой районов и фуксией ИЭЗ не спутать вовсе.
  'fishing-ban': '#991b1b',
  // Плашка и сетка скачанного кадра. Тёмно-синяя, потому что кадр уходит
  // НАРУЖУ и ложится то на светлую сушу, то на тёмное море: белый текст обязан
  // читаться на обоих. Загрузчик пака требует от партнёрского цвета контраста
  // AA с белым — светлую плашку он не пропустит.
  'export-plate': '#0a1a2b',
}

/** Плашка подписи поверх карты. НЕ айдентика: тёмный прямоугольник нужен,
 *  чтобы жёлтая подпись фронта читалась (на белой плашке жёлтый исчезал бы). */
export const LABEL_PLATE_BG = 'rgba(15,23,42,0.85)'

/** Пятёрка изобат карты апвеллинга — начертание телеграм-картинки, не гамма.
 *
 *  Общая на все бренды намеренно: «та же карта» обязана оставаться той же, а
 *  цвета выбраны различимыми при дальтонизме (палитра Пола Тола). Раздел
 *  «Изобаты» с шагом красится ШКАЛОЙ ГЛУБИН с сервера и бренду подчиняется. */
export const ISOBATH_LEVEL_COLORS: Readonly<Record<number, string>> = {
  [-50]: '#88CCEE',
  [-100]: '#44AA99',
  [-200]: '#117733',
  [-500]: '#332288',
  [-1000]: '#AA4499',
}

/** Тип дна (оверлей ГГК-1000): класс модели 1 камень … 5 ил.
 *
 *  Общие на все бренды, как пятёрка изобат: цвет здесь — КАТЕГОРИЯ, и грунт
 *  обязан читаться одинаково на любом домене. Тона — конвенция литологических
 *  карт (песок жёлтый, гравий охристый, камень тёмно-бурый, ил серо-бурый),
 *  различимые по светлоте, чтобы класс читался и поверх синей батиметрии. */
export const SUBSTRATE_CLASS_COLORS: Readonly<Record<number, string>> = {
  1: '#4b3b33', // камень
  2: '#c47a2c', // грубый: гравий, галька
  3: '#f3d36b', // песок
  4: '#9db25a', // смешанный
  5: '#8c7b6a', // ил
}

/** Глубина не из пятёрки — серая волосяная линия. */
export const ISOBATH_FALLBACK_COLOR = '#9ca3af'

/** Операции судна: переход / поиск / промысел / дрейф / стоянка / нет данных.
 *
 *  Палитра РАЗЛИЧАЮЩАЯ, а не декоративная: по ней читают, что судно делало.
 *  Бренд вправе её сменить, но загрузчик пака требует различимости по светлоте
 *  — иначе трек в чужой гамме перестал бы читаться вовсе. */
export const DEFAULT_VESSEL_OPS: readonly string[] = [
  '#475569', // переход
  '#0891b2', // поиск
  '#ea580c', // промысел — акцент, ради него всё
  '#9333ea', // дрейф
  '#334155', // стоянка
  '#94a3b8', // нет данных (рисуется пунктиром)
]

/** Судно без операции — нейтральный серый. */
export const VESSEL_NEUTRAL_COLOR = '#64748b'

/** Палитра inferno (как в eln): тёмный низ → жёлтый верх.
 *
 *  Сейчас её никто не красит: «Траектории по HSI» ушли на шкалу самого поля HSI
 *  (правка владельца 16.08.2026) — усик лежит поверх этого растра и обязан
 *  совпадать с ним, а шкала HSI смысловая и бренду не подчиняется. Ключ пака
 *  оставлен: он назван по сносу косяков, и трек косяка — его законное место. */
export const DEFAULT_DRIFT_RAMP: readonly string[] = [
  '#000004',
  '#420a68',
  '#932667',
  '#dd513a',
  '#fcffa4',
]

function rgb(c: readonly number[]): string {
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

/** Тот же цвет с прозрачностью — плашки и линии сетки кадра рисуются сквозными.
 *
 *  Отдельная функция, потому что альфа в бренд-паке запрещена (`hex_to_channels`
 *  отвергает восьмизначный hex): прозрачность — свойство МЕСТА, где цвет
 *  применяют, а не самого цвета. */
export function withAlpha(color: string, alpha: number): string {
  const nums = color.match(/\d+/g)
  if (!nums || nums.length < 3) return color
  return `rgba(${nums[0]},${nums[1]},${nums[2]},${alpha})`
}

/** Цвет линии карты для бренда домена. */
export function mapColor(brand: SiteBrand | null | undefined, key: MapColorKey): string {
  const own = brand?.map?.[key]
  if (Array.isArray(own) && own.length === 3 && typeof own[0] === 'number') {
    return rgb(own as number[])
  }
  return DEFAULT_MAP_COLORS[key]
}

/** Шкала сноса для бренда домена. */
const RAMP_DEFAULTS: Record<MapRampKey, readonly string[]> = {
  drift: DEFAULT_DRIFT_RAMP,
  'vessel-ops': DEFAULT_VESSEL_OPS,
}

export function mapRamp(brand: SiteBrand | null | undefined, key: MapRampKey): string[] {
  const own = brand?.map?.[key]
  if (Array.isArray(own) && own.length >= 2 && Array.isArray(own[0])) {
    return (own as number[][]).map(rgb)
  }
  return [...RAMP_DEFAULTS[key]]
}

// --------------------------------------------------------------------------- //
// Модульный регистр бренда
// --------------------------------------------------------------------------- //
//
// Гамму читают не только компоненты: слои MapLibre строит императивный код, а
// шевроны и подписи рисуются в canvas 2D — контекста React там нет. Ровно по
// этой причине в кабинете уже живёт `setLang`/`getLang`; здесь тот же приём.
//
// Значение ставится один раз, когда приезжает ответ `/cabinet/site`, — то есть
// до того, как человек дойдёт до карты. Промежуточный кадр рисуется нашей
// гаммой: это заметно лучше, чем пустая карта в ожидании ответа.

let current: SiteBrand | null = null

export function applyBrandMapColors(brand: SiteBrand | null): void {
  current = brand
}

/** Цвет линии карты текущего домена. */
export function mapColorOf(key: MapColorKey): string {
  return mapColor(current, key)
}

/** Шкала сноса текущего домена. */
export function driftRamp(): string[] {
  return mapRamp(current, 'drift')
}

/** Палитра операций судна текущего домена. */
export function vesselOpsRamp(): string[] {
  return mapRamp(current, 'vessel-ops')
}
